import type { ReactNode } from 'react';

import { fmtAMD } from '@/lib/format';
import { useI18n } from '@/providers/i18n';

import styles from './OrderSummary.module.css';

export interface OrderSummaryItem {
  key: string;
  label: string;
  qty: number;
}

interface OrderSummaryProps {
  orderRef: string;
  items?: OrderSummaryItem[];
  totalQty?: number;
  subtotal: number;
  fee: number;
  total: number;
  /** Optional CTAs / buttons rendered below the total row. */
  children?: ReactNode;
}

/**
 * Sticky right-rail order summary.
 *
 * Two modes (mutually compatible):
 *  - Tickets page: pass `totalQty` for a single aggregate "QTY × N" line.
 *  - Checkout page: pass `items` to enumerate each line item.
 *
 * `children` is a slot for the CTA(s) — Tickets shows the CHECKOUT button,
 * Checkout omits it (totals only).
 */
export default function OrderSummary({
  orderRef,
  items,
  totalQty,
  subtotal,
  fee,
  total,
  children,
}: OrderSummaryProps) {
  const { t } = useI18n();
  const hasItems = items && items.length > 0;
  const totalDisplay = total > 0 ? fmtAMD(total) : '0 AMD';
  const feeDisplay = fee > 0 ? fmtAMD(fee) : '—';

  return (
    <aside className={styles.root}>
      <div className="line-row">
        <span>ORDER</span>
        <span className="line" />
        <span>{orderRef}</span>
      </div>

      {hasItems &&
        items.map((it) => (
          <div className={styles.line} key={it.key}>
            <span className={styles.label}>{it.label}</span>
            <span className={styles.value}>× {it.qty}</span>
          </div>
        ))}

      {totalQty != null && (
        <div className={styles.line}>
          <span className={styles.label}>{t('label.qty')}</span>
          <span className={styles.value}>× {totalQty}</span>
        </div>
      )}

      <div className={styles.line}>
        <span className={styles.label}>{t('label.subtotal')}</span>
        <span className={styles.value}>{fmtAMD(subtotal)}</span>
      </div>
      <div className={styles.line}>
        <span className={styles.label}>{t('label.fee')}</span>
        <span className={styles.value}>{feeDisplay}</span>
      </div>
      <div className={`${styles.line} ${styles.total}`}>
        <span>{t('label.total')}</span>
        <span className={styles.value}>{totalDisplay}</span>
      </div>

      {children && <div className={styles.actions}>{children}</div>}
    </aside>
  );
}
